import { useCallback, useState } from "react";
import { api } from "@/services/api";
import { mergeRecord, buildTimedOutStatuses } from "@/lib/polling";
import { usePollerRegistry } from "@/hooks/usePollerRegistry";

export type IndexingJobStatus = "Queued" | "Indexing" | "Done" | "Error";

export interface IndexingJobInfo {
  status: IndexingJobStatus;
  queue_position?: number;
  error?: string;
}

export interface IndexingFileEntry {
  filename: string;
  document_id: string;
}

interface EnqueueIndexingJobInput {
  document_id: string;
  filename: string;
  doc_action: string;
}

interface EnqueueIndexingResponse {
  enqueued: { document_id: string; filename: string }[];
  queue_size: number;
}

interface IndexingStatusResponse {
  statuses: Record<string, IndexingJobInfo>;
  all_done: boolean;
  queue_size: number;
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 120; // 10 minutes

export function useIndexingQueue() {
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, IndexingJobInfo>>({});
  const [queueSize, setQueueSize] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const { run, stop, stopAll } = usePollerRegistry();

  const stopPolling = useCallback(() => {
    stop("main");
    setIsPolling(false);
  }, [stop]);

  const startPolling = useCallback(
    (docIds: string[], onAllDone?: (statuses: Record<string, IndexingJobInfo>) => void) => {
      setIsPolling(true);
      run<IndexingStatusResponse>("main", {
        fetchStatus: () => {
          const params = docIds.join(",");
          return api.get<IndexingStatusResponse>(
            `/api/indexing/status?document_ids=${encodeURIComponent(params)}`,
          );
        },
        isDone: (data) => data.all_done,
        onUpdate: (data) => {
          setDocumentStatuses((prev) => mergeRecord(prev, data.statuses));
          setQueueSize(data.queue_size);
        },
        onDone: (data) => {
          setIsPolling(false);
          onAllDone?.(data.statuses);
        },
        onTimeout: (lastData) => {
          const timedOutStatuses = buildTimedOutStatuses<IndexingJobInfo>(
            docIds,
            lastData,
            (status) => status === "Queued" || status === "Indexing",
            { status: "Error", error: "Indexing timed out" },
          );
          setDocumentStatuses((prev) => mergeRecord(prev, timedOutStatuses));
          setIsPolling(false);
          onAllDone?.(timedOutStatuses);
        },
        onError: (err) => console.error("Indexing queue poll error:", err),
        intervalMs: POLL_INTERVAL_MS,
        maxAttempts: MAX_POLLS,
      });
    },
    [run],
  );

  const enqueue = useCallback(
    async (
      jobs: EnqueueIndexingJobInput[],
      projectId?: string,
    ): Promise<IndexingFileEntry[]> => {
      const response = await api.post<EnqueueIndexingResponse>("/api/indexing/enqueue", {
        project_id: projectId,
        jobs,
      });

      const mapping: IndexingFileEntry[] = response.enqueued.map((e) => ({
        filename: e.filename,
        document_id: e.document_id,
      }));

      setQueueSize(response.queue_size);

      // Initialise all as Queued immediately
      const initial: Record<string, IndexingJobInfo> = {};
      mapping.forEach((m) => {
        initial[m.document_id] = { status: "Queued" };
      });
      setDocumentStatuses(initial);

      return mapping;
    },
    [],
  );

  const reset = useCallback(() => {
    stopAll();
    setIsPolling(false);
    setDocumentStatuses({});
    setQueueSize(0);
  }, [stopAll]);

  return {
    documentStatuses,
    queueSize,
    isPolling,
    enqueue,
    startPolling,
    stopPolling,
    reset,
  };
}
